import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
import {
  ArrowLeft, CircleNotch, CheckCircle, XCircle, ChartBar, Target, Lightning
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const paramLabels = {
  oe: "Odd/Even",
  hl: "High/Low",
  primes: "Primes",
  boundary: "Boundary",
  consecutive: "Consecutive",
  round_count: "Round (×5)",
  mult3: "Mult of 3",
  sum_bucket: "Sum Range",
  spread_bucket: "Spread Range",
  repeat_count: "Repeat Count",
  gap_composition: "Gap Composition",
  dg_coverage: "Digit Group Coverage",
  hot_cold: "Hot/Cold Mix",
  ls_oe: "LS Odd/Even",
  decade_max: "Decade Clustering",
};

const hitRateColor = (rate) => {
  if (rate >= 40) return "text-emerald-700 bg-emerald-50";
  if (rate >= 25) return "text-amber-700 bg-amber-50";
  return "text-red-700 bg-red-50";
};

export default function DNAValidation() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDraw, setExpandedDraw] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/analysis/dna-validation`)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="dna-validation-loading">
      <CircleNotch size={40} className="animate-spin text-blue-600" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-500">Failed to load data</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dna-validation-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-900" data-testid="back-button">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">W61: DNA Edge Validation</h1>
            <p className="text-xs text-gray-500">
              Backtesting DNA structural edges against actual {data.year} draw results
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6 space-y-5">


        <WindowNarrative windowId="W61" {...NARRATIVES.W61} className="mb-5" />
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="summary-cards">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-gray-500 uppercase font-bold">Draws Tested</p>
            <p className="text-3xl font-black text-gray-900 mt-1">{data.total_draws_tested}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-gray-500 uppercase font-bold">Edges Found</p>
            <p className="text-3xl font-black text-gray-900 mt-1">{data.total_edges_found}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-gray-500 uppercase font-bold">Total Hits</p>
            <p className="text-3xl font-black text-emerald-700 mt-1">{data.total_hits}</p>
          </div>
          <div className={`border rounded-lg p-4 text-center ${hitRateColor(data.overall_hit_rate)}`}>
            <p className="text-[10px] uppercase font-bold opacity-70">Overall Hit Rate</p>
            <p className="text-3xl font-black mt-1">{data.overall_hit_rate}%</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <p className="text-[10px] text-gray-500 uppercase font-bold">Avg Edges/Draw</p>
            <p className="text-3xl font-black text-gray-900 mt-1">{data.avg_edges_per_draw}</p>
          </div>
        </div>

        {/* Scorecard by Parameter */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="scorecard">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900">Parameter Scorecard</h2>
            <p className="text-[10px] text-gray-400">How often did each DNA edge parameter correctly predict the next draw? Sorted by hit rate.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2 text-left font-bold text-gray-500">Parameter</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Hits</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Misses</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Total</th>
                  <th className="px-4 py-2 text-center font-bold text-gray-500">Hit Rate</th>
                  <th className="px-4 py-2 text-left font-bold text-gray-500">Performance</th>
                </tr>
              </thead>
              <tbody>
                {data.scorecard.map((s) => {
                  const barW = Math.min(100, s.hit_rate);
                  return (
                    <tr key={s.param} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-bold text-gray-800">{paramLabels[s.param] || s.param}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-emerald-700 font-bold">{s.hits}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-red-500">{s.misses}</td>
                      <td className="px-4 py-2.5 text-center font-mono text-gray-600">{s.total}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${hitRateColor(s.hit_rate)}`}>
                          {s.hit_rate}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="w-full bg-gray-100 rounded-full h-3 relative">
                          <div
                            className={`h-3 rounded-full ${s.hit_rate >= 40 ? 'bg-emerald-500' : s.hit_rate >= 25 ? 'bg-amber-400' : 'bg-red-400'}`}
                            style={{ width: `${barW}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Draw-by-Draw Results */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="draw-results">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-900">Draw-by-Draw Validation</h2>
            <p className="text-[10px] text-gray-400">Click any row to see which edges hit/missed for that draw</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-bold text-gray-500">Draw</th>
                  <th className="px-3 py-2 text-left font-bold text-gray-500">Date</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">Balls</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">DNA Matches</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">Edges</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">Hits</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">Hit Rate</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">Next Draw</th>
                </tr>
              </thead>
              <tbody>
                {[...data.draw_results].reverse().map((dr) => {
                  const isExpanded = expandedDraw === dr.draw_number;
                  return (
                    <tr key={dr.draw_number} className="contents">
                      <td colSpan={8} className="p-0">
                        <div
                          className={`grid grid-cols-8 items-center cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${isExpanded ? 'bg-indigo-50' : ''}`}
                          onClick={() => setExpandedDraw(isExpanded ? null : dr.draw_number)}
                          data-testid={`draw-row-${dr.draw_number}`}
                        >
                          <div className="px-3 py-2.5 font-mono font-bold text-gray-700">#{dr.draw_number}</div>
                          <div className="px-3 py-2.5 text-gray-600">{dr.draw_date}</div>
                          <div className="px-3 py-2.5 text-center">
                            <div className="flex gap-0.5 justify-center">
                              {dr.balls.map((b, j) => (
                                <span key={j} className="inline-flex w-6 h-6 rounded-full bg-[#002FA7] text-white items-center justify-center text-[10px] font-bold">{b}</span>
                              ))}
                            </div>
                          </div>
                          <div className="px-3 py-2.5 text-center font-mono">{dr.dna_matches}</div>
                          <div className="px-3 py-2.5 text-center font-mono font-bold">{dr.edges_found}</div>
                          <div className="px-3 py-2.5 text-center font-mono font-bold text-emerald-700">{dr.edges_hit}</div>
                          <div className="px-3 py-2.5 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${hitRateColor(dr.hit_rate)}`}>
                              {dr.hit_rate}%
                            </span>
                          </div>
                          <div className="px-3 py-2.5 text-center">
                            <div className="flex gap-0.5 justify-center">
                              {dr.next_balls.map((b, j) => (
                                <span key={j} className="inline-flex w-6 h-6 rounded-full bg-gray-200 text-gray-700 items-center justify-center text-[10px] font-bold">{b}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Expanded edge details */}
                        {isExpanded && dr.edge_results.length > 0 && (
                          <div className="bg-indigo-50/50 px-4 py-3 border-b border-indigo-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {dr.edge_results.map((e, idx) => (
                                <div key={idx} className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${e.hit ? 'bg-emerald-100 border border-emerald-300' : 'bg-red-50 border border-red-200'}`}>
                                  {e.hit ? <CheckCircle size={16} className="text-emerald-600" weight="fill" /> : <XCircle size={16} className="text-red-500" weight="fill" />}
                                  <div className="flex-1">
                                    <span className="font-bold text-gray-800">{paramLabels[e.param] || e.param}</span>
                                    <div className="text-[10px] text-gray-500">
                                      Predicted: <strong>{String(e.value)}</strong> ({e.dna_pct}% DNA vs {e.overall_pct}% overall, +{e.edge}%)
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                      Actual: <strong className={e.hit ? 'text-emerald-700' : 'text-red-600'}>{String(e.actual)}</strong>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Interpretation */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-5" data-testid="interpretation">
          <h3 className="font-bold text-indigo-900 mb-2">How to Read This</h3>
          <div className="space-y-1.5 text-xs text-indigo-800">
            <p>For each {data.year} draw, we find historical sequences with similar DNA (structural shape). We then identify parameters where the "what came next" distribution differs from overall by 5%+ (the "edges").</p>
            <p>A <strong>hit</strong> means the actual next draw matched the DNA edge's predicted value. A <strong>miss</strong> means it didn't.</p>
            <p>Parameters with <span className="font-bold text-emerald-700">40%+ hit rate</span> show real predictive value. <span className="font-bold text-amber-700">25-40%</span> is marginal. <span className="font-bold text-red-600">Below 25%</span> is noise.</p>
            <p>Running since Jan 2026 — by mid-year (~50 draws) the pattern will be statistically meaningful.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
